import type { TokenAllowanceData } from 'lib/utils/allowances';
import type { Address } from 'viem';
import { deduplicateArray } from '.';
import { CHAIN_SELECT_MAINNETS } from './chains';
import { isApprovalTokenEvent, type TokenEvent } from './events';

export type Exploit = {
  slug: string;
  name: string;
  description: string;
  longDescription?: string;
  date: string;
  amount: number;
  fixed?: boolean;
  addresses: ExploitAddress[];
  metaArticleUrls: string[];
};

export type ExploitAddress = {
  chainId: number;
  address: Address;
};

export type ExploiStatus = 'affected' | 'safe' | 'previously_affected';

const ORGANISATION = 'RevokeCash';
const REPOSITORY = 'approval-exploit-list';
export const REPORT_EXPLOIT_URL = `https://github.com/${ORGANISATION}/${REPOSITORY}/issues/new?assignees=&labels=submission%2Cexploit&projects=&template=REPORT-EXPLOIT.yml`;

export const getGlobalExploitStats = (exploits: Exploit[]) => {
  const totalAmount = formatExploitAmount(Math.floor(exploits.reduce((prev, curr) => prev + curr.amount, 0)));
  const earliestYear = exploits.at(-1)!.date.slice(0, 4);

  return { totalAmount, earliestYear };
};

export const getUniqueChainIds = (exploit: Exploit): number[] => {
  const sorter = (a: number, b: number) => {
    const indexOfA = CHAIN_SELECT_MAINNETS.indexOf(a);
    const indexOfB = CHAIN_SELECT_MAINNETS.indexOf(b);
    if (indexOfA === -1) return 1;
    if (indexOfB === -1) return -1;
    return indexOfA - indexOfB;
  };

  const allChainIds = exploit.addresses.map((address) => address.chainId);
  const uniqueChainIds = deduplicateArray(allChainIds);
  const sortedChainIds = uniqueChainIds.sort(sorter);

  return sortedChainIds;
};

export const getExploitStatus = (
  events: TokenEvent[],
  allowances: TokenAllowanceData[],
  exploit: Exploit,
): ExploiStatus => {
  if (isAffectedByExploit(allowances, exploit)) return 'affected';
  if (isAffectedByExploit(events.filter(isApprovalTokenEvent), exploit)) return 'previously_affected';
  return 'safe';
};

interface EventOrAllowance {
  payload?: {
    spender: Address;
  };
  chainId: number;
}

const isAffectedByExploit = (eventsOrAllowances: Array<EventOrAllowance>, exploit: Exploit) => {
  return eventsOrAllowances.some((eventOrAllowance) =>
    exploit.addresses.some(
      (exploitAddress) =>
        eventOrAllowance.payload?.spender === exploitAddress.address &&
        eventOrAllowance.chainId === exploitAddress.chainId,
    ),
  );
};

export const formatExploitAmount = (amountInMillions: number | null) => {
  if (amountInMillions === null) return 'Unknown';
  if (amountInMillions === 0) return `$${amountInMillions}`;
  if (amountInMillions < 1) return `$${amountInMillions * 1000}k`;
  return `$${amountInMillions}M`;
};
